import { Currency, Money, ItemRef, DeviceStrategy, Cart, DiscountTable } from '../types/PricingCalculator';

export function isFree(rrdbISO: string, now = new Date()): boolean {
    const dt = new Date(rrdbISO);
    const twoYearsMs = 2 * 365 * 24 * 3600 * 1000;
    return (now.getTime() - dt.getTime()) < twoYearsMs;
}

export function needsPrinter(strategy: DeviceStrategy, screen?: ItemRef): boolean {
    if (strategy.existingSmartphone) return true;
    if (!screen) return true;
    return !Boolean(screen.meta?.builtInPrinter);
}

export function compatiblePrinters(strategy: DeviceStrategy, allPrinters: ItemRef[]): ItemRef[] {
    if (!strategy.existingSmartphone) return allPrinters;
    // Prefer Bluetooth for phones
    return allPrinters.filter(p => p.meta?.bt === true || (strategy.phone?.otg && p.meta?.usb === true));
}

export function requiredAdapters(strategy: DeviceStrategy, chosen: { printer?: ItemRef; scanner?: ItemRef }): ItemRef[] {
    const out: ItemRef[] = [];
    if (!strategy.existingSmartphone) return out;

    const phonePort = strategy.phone?.port;
    const phoneOTG = strategy.phone?.otg;
    const wiredNeeds =
        (chosen.printer?.meta?.usb === true || chosen.scanner?.meta?.usb === true) &&
        !chosen.printer?.meta?.bt && !chosen.scanner?.meta?.bt;

    if (wiredNeeds && phoneOTG && phonePort) {
        // Example adapter mapping
        const adapter = adapterForPort(phonePort);
        if (adapter) out.push(adapter);
    }
    return out;
}

function adapterForPort(phonePort: "usb_c" | "micro_usb" | "lightning"): ItemRef | null {
    const adapters: Record<string, ItemRef> = {
        usb_c: {
            id: "adapter-usb-c",
            name: "USB-C to USB-A Adapter",
            description: "Adapter to connect USB-C devices to USB-A accessories",
            category: "adapter",
            price: { value: 5, currency: "USD" },
            availability: "Rwanda",
            link: "#",
            meta: { from: "usb_c", to: "usb_a" }
        },
        micro_usb: {
            id: "adapter-micro-usb",
            name: "Micro-USB to USB-A Adapter",
            description: "Adapter to connect Micro-USB devices to USB-A accessories",
            category: "adapter",
            price: { value: 3, currency: "USD" },
            availability: "Rwanda",
            link: "#",
            meta: { from: "micro_usb", to: "usb_a" }
        },
        lightning: {
            id: "adapter-lightning",
            name: "Lightning to USB-A Adapter",
            description: "Adapter to connect Lightning devices to USB-A accessories",
            category: "adapter",
            price: { value: 15, currency: "USD" },
            availability: "Rwanda",
            link: "#",
            meta: { from: "lightning", to: "usb_a" }
        }
    };
    return adapters[phonePort] || null;
}

export function applyDiscount(baseMonthly: number, months: 1 | 6 | 12 | 24, discounts: DiscountTable): number {
    const rate = discounts[String(months) as keyof DiscountTable] ?? 0;
    return baseMonthly * months * (1 - rate);
}

export function convert(m: Money, fx: Record<Currency, number>, store: Currency): number {
    // Convert from source currency to target/store currency using rate ratios.
    // Example: USD->RWF with fx { USD:1, RWF:1500 } => value * (1500 / 1)
    return m.value * (fx[store] / fx[m.currency]);
}

export function priceSummary(cart: Cart, storeCurrency: Currency = "RWF"): Cart["totals"] {
    const hwItems = [
        cart.selections.screen,
        cart.selections.printer,
        cart.selections.scanner,
        ...(cart.selections.adapters ?? [])
    ].filter(Boolean) as ItemRef[];

    const hwSum = hwItems.reduce((s, it) => s + convert(it.price, cart.fx, storeCurrency), 0);

    let swSum = 0;
    let additionalUsersSum = 0;
    let discountRate: number | undefined = undefined;

    if (!cart.eligibility.isFree && cart.selections.software) {
        const baseMonthly = convert(cart.selections.software.price, cart.fx, storeCurrency);
        const table: DiscountTable = { "1": 0, "6": 0.10, "12": 0.20, "24": 0.35 };
        discountRate = table[String(cart.termMonths) as keyof DiscountTable];
        swSum = applyDiscount(baseMonthly, cart.termMonths, table);

        // Calculate additional user charges (freeUsers = 2, charge 5000 RWF per additional user)
        const freeUsers = 2;
        const additionalUsers = Math.max(0, cart.userCount - freeUsers);
        additionalUsersSum = additionalUsers * 5000; // 5000 RWF per additional user
    }

    const totalSum = hwSum + swSum + additionalUsersSum;

    return {
        hardware: { value: Math.round(hwSum), currency: storeCurrency },
        software: { value: Math.round(swSum), currency: storeCurrency },
        additionalUsers: { value: Math.round(additionalUsersSum), currency: storeCurrency },
        grandTotal: { value: Math.round(totalSum), currency: storeCurrency },
        ...(discountRate !== undefined ? { discountRate } : {})
    };
}

export function formatCurrency(value: number, currency: Currency): string {
    if (currency === "RWF") {
        return `${value.toLocaleString()} RWF`;
    }
    return `$${value.toFixed(2)}`;
}

export function canProceedToNextStep(step: string, cart: Cart): boolean {
    switch (step) {
        case 'eligibility':
            return cart.eligibility.rrdbRegistrationDateISO !== '';
        case 'device-strategy':
            return cart.strategy.existingSmartphone !== undefined;
        case 'hardware':
            if (cart.strategy.existingSmartphone) {
                return !!cart.selections.printer;
            } else {
                return !!cart.selections.screen && !!cart.selections.printer;
            }
        case 'plan':
            return cart.eligibility.isFree || cart.termMonths !== undefined;
        case 'user-count':
            return cart.userCount > 0;
        case 'summary':
            return true;
        default:
            return false;
    }
}

export function getValidationMessage(step: string, cart: Cart): string {
    switch (step) {
        case 'eligibility':
            return "pricingCalc.validation.selectEligibility";
        case 'device-strategy':
            return "pricingCalc.validation.selectDeviceStrategy";
        case 'hardware':
            if (cart.strategy.existingSmartphone) {
                return "pricingCalc.validation.selectPrinter";
            } else {
                if (!cart.selections.screen && !cart.selections.printer) {
                    return "pricingCalc.validation.selectScreenPrinter";
                } else if (!cart.selections.screen) {
                    return "pricingCalc.validation.selectScreen";
                } else if (!cart.selections.printer) {
                    return "pricingCalc.validation.selectPrinter";
                }
                return "";
            }
        case 'plan':
            if (cart.eligibility.isFree) {
                return "pricingCalc.validation.freeSoftwareNoPlan";
            }
            return "pricingCalc.validation.selectPlan";
        case 'user-count':
            return "pricingCalc.validation.enterUserCount";
        case 'summary':
            return "";
        default:
            return "pricingCalc.validation.completeCurrentStep";
    }
}
